import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ClientState } from "../../types";
import { axiosObj } from "../../../config/apiClients";
import { Client, ClientServiceAccess, MoneyTransaction } from "../../../utils/types";
import { ClientFilterParams } from "../../../utils/interface";

const initialState: ClientState = {
  isLoading: false,
  isAuxLoading: false,
  isClientDropdownLoading: false,
  isClientDetailLoading: false,
  clients: [],
  client: null,
  clientDropdown: [],
  clientDeviceInfo: [],
  activeTemporaryCredits: [],
  clientKycRecord: null,
  serviceAccess: null,
  isServiceLoading: false,
  clientDropdownSearch: "",
  clientDropdownPagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 0,
    hasNextPage: false,
  },
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false
  }
}

type Postion = {
  userId: string;
  type: string;
  tradeSymbol: string;
  amount: number;
  price: number;
  comment: string;
}

export const getAllClients = createAsyncThunk(
  "/user/getAll",
  async ({ paramPage, paramLimit, filter, search, lockedUsers, userType, isBlacklist }: ClientFilterParams, { rejectWithValue }) => {
    try {
      const url = `/user/getAll${paramPage && paramLimit ? `?paramPage=${paramPage}&paramLimit=${paramLimit}` : ""}${filter ? `&source=${filter}` : ""}${lockedUsers ? `&lockedUsers=${lockedUsers}` : ""}${search ? `&search=${search}` : ""}${userType ? `&userType=${userType}` : ""}${isBlacklist ? `&blacklistUsers=${isBlacklist}` : ""}`
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Users' });
    }
  }
)

interface DropdownListParams {
  search?: string;
  paramPage?: number;
  paramLimit?: number;
}

const EMPTY_DROPDOWN_PAGE = {
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  allUsers: [],
};

export const getAllClientsForDropdown = createAsyncThunk(
  "/user/getDropdownList",
  async (
    { search, paramPage = 1, paramLimit = 20 }: DropdownListParams = {},
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("paramPage", String(paramPage));
      params.set("paramLimit", String(paramLimit));
      if (search) params.set("search", search);

      const response = await axiosObj.get(
        `user/getDropdownList?${params.toString()}`,
      );
      return response.data;
    } catch (error: any) {
      // The backend returns 404 when no users match — treat that as an
      // empty page rather than a hard error so the dropdown just shows
      // "no results".
      if (error?.response?.status === 404) {
        return { payload: { ...EMPTY_DROPDOWN_PAGE, page: paramPage } };
      }
      return rejectWithValue(error || { message: "Failed to Fetch Users" });
    }
  },
)

export const assignReferee = createAsyncThunk(
  "/user/referral/assign",
  async (
    { referrerId, refereeId }: { referrerId: string; refereeId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.post("/user/referral/assign", {
        referrerId,
        refereeId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to assign referee" });
    }
  },
)

export const createClient = createAsyncThunk(
  "/user/create",
  async (client: Client, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/create', client);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add User' });
    }
  }
)

export const updateClient = createAsyncThunk(
  "/user/update",
  async (client: Client, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/user/update', client);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update User' });
    }
  }
)

export const updateClientAccountType = createAsyncThunk(
  "/user/updateClientAccountType",
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/user/updateUserAccountType', { userId: clientId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update User Account Type' });
    }
  }
)

export const deleteClient = createAsyncThunk(
  "/user/delete",
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete('/user/delete', { data: { _id: clientId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to delete User' });
    }
  }
)

export const getClientById = createAsyncThunk(
  "/user/getById",
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/getById', { _id: clientId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to delete User' });
    }
  }
)

export const createMoneyTransaction = createAsyncThunk(
  "/user/moneyTransactions/create",
  async (transaction: MoneyTransaction, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/moneyTransactions/create', transaction);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add Money Transaction' });
    }
  }
)

export const createBuyPosition = createAsyncThunk(
  "/trade/add-gold",
  async (position: Postion, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/trade/add-gold', position);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add Gold in the user account.' });
    }
  }
)

export const createSellPosition = createAsyncThunk(
  "/trade/sell-gold",
  async (position: Postion, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/trade/sell-gold', position);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Sell Gold in the user account.' });
    }
  }
)

export const getClientKYCRecord = createAsyncThunk(
  "/user/getKycRecord",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/getKycRecord', { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch User Kyc Record.' });
    }
  }
)

export const getClientDevices = createAsyncThunk(
  "/user/getClientDevices",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/getUserDevices', { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch User Devices.' });
    }
  }
)

export const getClientServiceAvailability = createAsyncThunk(
  "/user/getclientServiceAvailability",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/service-access', { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch User Service Access.' });
    }
  }
)

export const updateClientServiceAvailability = createAsyncThunk(
  "/user/updateClientServiceAvailability",
  async (
    {
      clientId,
      service,
      enabled,
    }: {
      clientId: string;
      service: keyof ClientServiceAccess;
      enabled: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosObj.patch('/user/service-access', {
        userId: clientId,
        service,
        enabled,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to update User Service Access.' });
    }
  }
);

export const getUserActiveTemporaryCredits = createAsyncThunk(
  "/user/temporary-credits/active",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/user/temporary-credits/active', { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch Active temporary credits.' });
    }
  }
)

const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    setClient: (state, action) => {
      state.client = action.payload;
    },
    resetClient: (state) => {
      state.client = null;
    },
    removeClient: (state, action) => {
      const clientId = action.payload;
      state.clients = state.clients.filter((client) => client._id !== clientId);
    },
    resetClients(state) {
      state.clients = [];
      state.pagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
    resetKycRecord(state) {
      state.clientKycRecord = null;
    },
    toggleClientServiceAccess(
      state,
      action: PayloadAction<{ service: keyof ClientServiceAccess; enabled: boolean }>
    ) {
      const { service, enabled } = action.payload;
      if (state.serviceAccess) {
        state.serviceAccess[service] = enabled;
      }
    },
    resetActiveTemporaryCredits(state) {
      state.activeTemporaryCredits = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllClients.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = action.payload.payload.kAllUsers.allUsers;
        state.pagination = {
          totalItems: action.payload.payload.kAllUsers.totalItems,
          currentPage: action.payload.payload.kAllUsers.page,
          totalPages: action.payload.payload.kAllUsers.totalPages,
          hasNextPage: action.payload.payload.kAllUsers.hasNextPage
        };
      })
      .addCase(getAllClients.rejected, (state) => {
        state.isLoading = false;
        state.clients = [];
      })
      .addCase(getClientById.pending, (state) => {
        state.isClientDetailLoading = true;
      })
      .addCase(getClientById.fulfilled, (state, action) => {
        state.isClientDetailLoading = false;
        state.client = action.payload.payload[0];
      })
      .addCase(getClientById.rejected, (state) => {
        state.isClientDetailLoading = false;
        state.client = null;
      })
      .addCase(createClient.pending, (state) => {
        state.isClientDetailLoading = true;
      })
      .addCase(createClient.fulfilled, (state) => {
        state.isClientDetailLoading = false;
      })
      .addCase(createClient.rejected, (state) => {
        state.isClientDetailLoading = false;
      })
      .addCase(updateClient.pending, (state) => {
        state.isClientDetailLoading = true;
      })
      .addCase(updateClient.fulfilled, (state) => {
        state.isClientDetailLoading = false;
        state.client = null;
      })
      .addCase(updateClient.rejected, (state) => {
        state.isClientDetailLoading = false;
      })
      .addCase(deleteClient.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteClient.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createMoneyTransaction.pending, (state) => {
        state.isAuxLoading = true;
      })
      .addCase(createMoneyTransaction.fulfilled, (state) => {
        state.isAuxLoading = false;
      })
      .addCase(createMoneyTransaction.rejected, (state) => {
        state.isAuxLoading = false;
      })
      .addCase(getAllClientsForDropdown.pending, (state) => {
        state.isClientDropdownLoading = true;
      })
      .addCase(getAllClientsForDropdown.fulfilled, (state, action) => {
        state.isClientDropdownLoading = false;
        const p = action.payload?.payload ?? {};
        state.clientDropdown = Array.isArray(p.allUsers)
          ? p.allUsers
          : Array.isArray(p)
            ? p
            : [];
        state.clientDropdownSearch = action.meta.arg?.search ?? "";
        state.clientDropdownPagination = {
          totalItems: p.totalItems ?? state.clientDropdown.length,
          currentPage: p.page ?? action.meta.arg?.paramPage ?? 1,
          totalPages: p.totalPages ?? 1,
          hasNextPage: !!p.hasNextPage,
        };
      })
      .addCase(getAllClientsForDropdown.rejected, (state) => {
        state.isClientDropdownLoading = false;
      })
      .addCase(getUserActiveTemporaryCredits.pending, (state) => {
        state.isAuxLoading = true;
      })
      .addCase(getUserActiveTemporaryCredits.fulfilled, (state, action) => {
        state.isAuxLoading = false;
        state.activeTemporaryCredits = action.payload.payload;
      })
      .addCase(getUserActiveTemporaryCredits.rejected, (state) => {
        state.isAuxLoading = false;
      })
      .addCase(createBuyPosition.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createBuyPosition.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createBuyPosition.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createSellPosition.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createSellPosition.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createSellPosition.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateClientAccountType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateClientAccountType.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateClientAccountType.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getClientKYCRecord.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getClientKYCRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientKycRecord = action.payload.payload;
      })
      .addCase(getClientKYCRecord.rejected, (state) => {
        state.isLoading = false;
        state.clientKycRecord = null;
      })
      .addCase(getClientDevices.pending, (state) => {
        state.isAuxLoading = true;
      })
      .addCase(getClientDevices.fulfilled, (state, action) => {
        state.isAuxLoading = false;
        state.clientDeviceInfo = action.payload.payload;
      })
      .addCase(getClientDevices.rejected, (state) => {
        state.isAuxLoading = false;
        state.clientDeviceInfo = [];
      })
      .addCase(getClientServiceAvailability.pending, (state) => {
        state.isServiceLoading = true;
      })
      .addCase(getClientServiceAvailability.fulfilled, (state, action) => {
        state.isServiceLoading = false;
        state.serviceAccess = action.payload.payload;
      })
      .addCase(getClientServiceAvailability.rejected, (state) => {
        state.isServiceLoading = false;
        state.serviceAccess = null;
      });
  }
});

export const { setClient, removeClient, resetClients, resetKycRecord, toggleClientServiceAccess, resetActiveTemporaryCredits, resetClient } = clientSlice.actions;

export default clientSlice.reducer;